import { useTheme } from '../../context/ThemeContext';
import { DEFAULT_ET_LAYERS, ET_COLORS, type EtLayers, type EtResearchPayload } from '../../data/et-research';

export function EtResearchSection({ layers, onChange, onZoom, data, loading, error, onRetry }: {
  layers: EtLayers;
  onChange: (layers: EtLayers) => void;
  onZoom: (bounds: [number, number, number, number]) => void;
  data: EtResearchPayload | null;
  loading: boolean;
  error: string | null;
  onRetry: () => void;
}) {
  const { theme } = useTheme();
  const button = { background: 'none', border: `1px solid ${theme.border}`, color: theme.text, borderRadius: 4, padding: '5px 8px', fontSize: 11, cursor: 'pointer' };
  const summary = { color: theme.text, cursor: 'pointer' };
  const sourceName = (id: string) => data?.sources.find(s => s.id === id)?.title ?? id;
  return <div data-testid="et-research" style={{ padding: '8px 12px 12px', color: theme.textSecondary, fontSize: 11, lineHeight: 1.5 }}>
    <p style={{ margin: '0 0 8px' }}>Energy Transfer source maps. Select either network or combine both; shared geometry is drawn once.</p>
    {(['powergen', 'intrastate'] as const).map(key => <label key={key} style={{ display: 'flex', alignItems: 'center', gap: 7, padding: '5px 0', cursor: 'pointer', color: theme.text }}>
      <input type="checkbox" checked={layers[key]} onChange={() => onChange({ ...layers, [key]: !layers[key] })} />
      <span>{key === 'powergen' ? 'PowerGen network' : 'Intrastate network'}</span>
    </label>)}
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, margin: '8px 0' }}>
      <button type="button" style={button} aria-pressed={layers.powergen && layers.intrastate} onClick={() => onChange({ ...DEFAULT_ET_LAYERS })}>Combined</button>
      <button type="button" style={{ ...button, opacity: data ? 1 : 0.5, cursor: data ? 'pointer' : 'default' }} disabled={!data} onClick={() => { if (data) onZoom(data.bounds); }}>Zoom to network</button>
    </div>
    <div aria-label="ET map legend" style={{ margin: '8px 0', fontSize: 10 }}>
      {[
        { key: 'powergen', text: 'PowerGen-associated routes', color: ET_COLORS.powergen },
        { key: 'intrastate', text: 'Additional Intrastate routes', color: ET_COLORS.intrastate },
        { key: 'lateral', text: 'Abilene laterals · unconfirmed', color: ET_COLORS.lateral },
      ].map(item => <div key={item.key} style={{ display: 'flex', alignItems: 'center', gap: 7, padding: '2px 0' }}>
        <span aria-hidden style={{ width: 18, borderTop: `3px ${item.key === 'lateral' ? 'dashed' : 'solid'} ${item.color}`, flexShrink: 0 }} />{item.text}
      </div>)}
      <div style={{ marginTop: 3 }}>Colors show source membership, including when only Intrastate is selected.</div>
    </div>
    {loading && <p role="status">Loading ET network and source details…</p>}
    {error && <div role="alert"><p>{error}</p><button type="button" style={button} onClick={onRetry}>Retry</button></div>}
    {!loading && !error && !data && <p>Source details load when this group is opened or enabled.</p>}
    {data && <>
      <p style={{ margin: '8px 0' }}>Reviewed {data.reviewedAt}. Solid routes: network; dashed routes: source-designated laterals. Click a route for its source records. These maps do not establish current service, available supply or tap rights.</p>
      <details style={{ marginTop: 10 }}>
        <summary style={summary}>Network sources</summary>
        {data.sources.filter(source => source.id === 'powergen' || source.id === 'intrastate').map(source => <div key={source.id} style={{ marginTop: 8 }}>
          <strong style={{ color: theme.text }}>{source.title}</strong><br />{source.dateLabel}
          <p style={{ margin: '4px 0' }}>{source.note}</p>
        </div>)}
        <p>Raw diameter fields have unconfirmed units. No route capacity or power estimate is inferred from the source filenames.</p>
      </details>
      {data.references.filter(reference => reference.sourceId === 'customer-map' || reference.sourceId === 'asset-deck').map(reference => <details key={reference.sourceId} style={{ marginTop: 10 }}>
        <summary style={summary}>{reference.title} · {reference.dateLabel}</summary>
        {reference.sections.map((section, index) => <div key={`${section.heading}:${index}`} style={{ marginTop: 8 }}>
          <strong style={{ color: theme.text }}>{section.heading}</strong>
          {section.text.map((text, i) => <p key={i} style={{ margin: '4px 0' }}>{text}</p>)}
        </div>)}
        {(reference.links ?? []).filter(link => /^https:\/\//i.test(link.url)).map(link => <div key={link.url}><a href={link.url} target="_blank" rel="noreferrer" style={{ color: theme.link }}>{link.label}</a></div>)}
      </details>)}
      <details style={{ marginTop: 10 }}>
        <summary style={summary}>System details · {data.systems.length}</summary>
        {data.systems.map(system => <details key={system.id} style={{ marginTop: 8, paddingTop: 6, borderTop: `1px solid ${theme.borderLight}` }}>
          <summary style={summary}>{system.name}</summary>
          {system.aliases.length > 0 && <p style={{ margin: '4px 0' }}>Source names: {system.aliases.join(' · ')}</p>}
          {system.facts.map((fact, i) => <p key={i} style={{ margin: '6px 0' }}>{fact.text}<br /><span style={{ fontSize: 10 }}>{sourceName(fact.sourceId)}{fact.page != null ? ` · p. ${fact.page}` : ''}</span></p>)}
        </details>)}
      </details>
      <details style={{ marginTop: 10 }}>
        <summary style={summary}>Existing map comparison</summary>
        <p>{data.productionComparison.scope} · {data.productionComparison.capturedAt}</p>
        {data.productionComparison.summary.map((text, i) => <p key={i}>{text}</p>)}
        {data.productionComparison.limitations.map((text, i) => <p key={i}>{text}</p>)}
      </details>
    </>}
  </div>;
}
